import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { loanOfficersQueryOptions } from '@/api/reference-api'
import type { Till } from '@/api/teller-types'
import { MoneyDisplay } from '@/components/money-display'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatBusinessDate } from '@/lib/datetime'

import { useAssignFieldTill } from './mutations'
import { TELLER_PERMISSIONS, useTellerPermissions } from './permissions'
import { TellerActionDialog } from './teller-action-dialog'

/** The reason is required and capped at 500 characters (`teller.reason_required`). */
const REASON_LIMIT = 500

/**
 * Handing a field drawer to a loan officer for a day.
 *
 * The warning on this dialog is worded against what the server actually enforces, which is narrower
 * than it sounds. `teller.till_not_empty` is raised only when the drawer holds cash **and** is
 * currently assigned to a *different* officer. An unassigned drawer still holding yesterday's
 * collections is accepted with no refusal at all, and a re-assignment to the same officer is always
 * accepted. Telling an operator that a drawer with cash in it "cannot change hands" would be a
 * promise the module does not keep, in precisely the case where the money is most at risk — so the
 * balance is put in front of them instead, and the judgement stays theirs.
 *
 * The assignment is not dual-controlled: it answers 204 and takes effect at once.
 */
export function AssignFieldTillDialog({
  till,
  branchId,
  open,
  onOpenChange,
}: {
  till: Till
  branchId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useTellerPermissions()
  const [officerId, setOfficerId] = useState('')
  const [forDate, setForDate] = useState('')
  const [reason, setReason] = useState('')

  const assign = useAssignFieldTill(till.id)

  // Reads the Access directory, which needs `admin.user.view`. Every shipped role holding
  // `teller.field_till.assign` (BranchManager, SuperAdministrator) also holds it, so this cannot be
  // an empty list that looks like "there are no officers" — but it can 403, and the panel says so.
  const officers = useQuery({
    ...loanOfficersQueryOptions(branchId),
    enabled: open && permissions.canViewStaff && Boolean(branchId),
  })

  useEffect(() => {
    if (!open) {
      setOfficerId('')
      setForDate('')
      setReason('')
      assign.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const holdsCash = till.cashOnHandMinor !== 0

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Assign the field till"
      description={`${till.tillNumber} · ${till.name}. An assignment is good for one named day.`}
      confirmLabel="Assign the drawer"
      isPending={assign.isPending}
      disabled={officerId === '' || forDate === '' || reason.trim() === '' || reason.length > REASON_LIMIT}
      error={assign.isError ? assign.error : null}
      onConfirm={() => {
        assign.mutate(
          { loanOfficerId: officerId, forDate, reason: reason.trim() },
          { onSuccess: () => onOpenChange(false) },
        )
      }}
    >
      {holdsCash ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>This drawer still holds cash</AlertTitle>
            <AlertDescription>
              <MoneyDisplay
                value={till.cashOnHandMinor}
                currency={till.currencyCode}
                className="inline-block text-left"
              />{' '}
              is in it now.{' '}
              {till.assignedUserId
                ? 'Because it is currently held by another officer, the server will refuse this with teller.till_not_empty until the cash is banked.'
                : 'It is unassigned, so the server will accept this assignment — the incoming officer inherits the balance, and any shortage in it, unless you bank the cash to the vault first.'}
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="assign-officer">Loan officer</Label>
        {!permissions.canViewStaff ? (
          <p className="text-xs text-warning-text">
            Listing staff needs <span className="font-mono">{TELLER_PERMISSIONS.userView}</span>,
            which your role does not hold, so there is nobody to choose from here.
          </p>
        ) : (
          <>
            <Select value={officerId === '' ? undefined : officerId} onValueChange={setOfficerId}>
              <SelectTrigger id="assign-officer">
                <SelectValue
                  placeholder={officers.isPending ? 'Reading the directory…' : 'Choose an officer'}
                />
              </SelectTrigger>
              <SelectContent>
                {(officers.data ?? []).map((officer) => (
                  <SelectItem key={officer.userId} value={officer.userId}>
                    {officer.fullName} · {officer.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">
              Only loan officers are listed. An officer without access to this branch is refused when
              you confirm — an officer who cannot act for a branch has no business holding its cash.
            </p>
          </>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="assign-date">For the day</Label>
        <Input
          id="assign-date"
          type="date"
          value={forDate}
          onChange={(event) => setForDate(event.target.value)}
        />
        <p className="text-xs text-muted">
          Today or later — a day that has passed is refused as{' '}
          <span className="font-mono">teller.assignment_date_in_past</span>. The drawer is live on
          that date and no other.
          {till.assignedForDate ? (
            <> It is currently assigned for {formatBusinessDate(till.assignedForDate)}.</>
          ) : null}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="assign-reason">Reason</Label>
        <Input
          id="assign-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          autoComplete="off"
          placeholder="Which round, which meeting, which route"
        />
        <p className="text-xs text-muted">
          Required, up to {REASON_LIMIT} characters. It is what the audit trail carries when somebody
          asks why an officer was carrying branch cash that day.
        </p>
      </div>
    </TellerActionDialog>
  )
}
